import {injectIntl} from "react-intl";
import React, {useContext, useEffect, useRef, useState} from "react";
import {VaprDeviceOverviewContext} from "./context/VaprDeviceOverviewContext";
import Loader from "../../loader/Loader";
import Row from "@amzn/meridian/row"
import Column from "@amzn/meridian/column";
import ConfigurationTabHeader from "../../commonui/ConfigurationTabHeader";
import {DraftContext} from "../../draftSave/context/DraftSaveContext";
import {ASCENDING, CONFIG_TYPE, OPERATION_COLOR, SEARCH_ROW_COLOR} from "../../util/Constants";
import {getAssetByVersion, getAssetVersions} from "../../resources/AssetResource";
import RollbackModal from "../../rollback/RollbackModal";
import {chainWalk, getAssetRefreshInterval, getVaprLocationTypeLabel, getVaprOrientationLabel,
    sortComparator} from "../../util/Util";
import PaginatedTable from "../../commonui/PaginatedTable";
import {TableCell, TableRow} from "@amzn/meridian/table";
import Text from "@amzn/meridian/text";
import Tooltip from "@amzn/meridian/tooltip";
import Icon from "@amzn/meridian/icon";
import arrowRightTokens from "@amzn/meridian-tokens/base/icon/arrow-right-large";
import {getLocalizedMessage} from "../../util/Localization";
import Input from "@amzn/meridian/input";
import Button from "@amzn/meridian/button";
import plusCircleTokens from "@amzn/meridian-tokens/base/icon/plus-circle";
import uploadTokens from "@amzn/meridian-tokens/base/icon/upload-small";
import trashTokens from "@amzn/meridian-tokens/base/icon/trash";
import Box from "@amzn/meridian/box";
import AddVaprLocationConfigurationModal from "./modal/AddVaprLocationConfigurationModal";
import DeleteConfigurationModal from "../../commonui/DeleteConfigurationModal";
import UploadFileModal from "../../commonui/UploadFileModal";
import {VaprLocationConfigCSVParser} from "../../util/Parsers";
import {GlobalStateContext} from "../../global/context/GlobalStateContext";
import VaprOrientationGuide from "./VaprOrientationGuide";

// Stored value -> display label. Shared with the CSV parser via Util, so the table, the dropdowns
// and the CSV all use one vocabulary.
const getLocationTypeDisplay = getVaprLocationTypeLabel;
const getOrientationDisplay = getVaprOrientationLabel;

// VAPR Configuration tab (mirrors ZancaSort). One config type today — Location Configuration
// ({label, locationType, orientation}) — staged in the shared draft and deployed to `locationMapping`.
const VaprDeviceConfiguration = ({intl}) => {
    const {viewModel, dispatch} = useContext(VaprDeviceOverviewContext);
    const {getDraftData, setDraftData, resetDraftData, getFilteredDraftData, isDataEdited} = useContext(DraftContext);
    const {showCustomAlert} = useContext(GlobalStateContext);

    const [selectedMac, setSelectedMac] = useState("");
    const [valueConfiguration, setValueConfiguration] = useState("");
    const [rollBackVersions, setRollbackVersions] = useState([]);
    const [isVersionsLoading, setIsVersionsLoading] = useState(false);
    const [selectedVersion, setSelectedVersion] = useState("");

    const [showRollbackModal, setShowRollbackModal] = useState(false)
    const [openAddLocationModal, setOpenAddLocationModal] = useState(false)
    const [openDeleteLocationModal, setOpenDeleteLocationModal] = useState(false)
    const [openLocationCSVModal, setOpenLocationCSVModal] = useState(false)

    const [locationIdSearchInput, setLocationIdSearchInput] = useState('');
    const [locationTypeSearchInput, setLocationTypeSearchInput] = useState('');
    const [orientationSearchInput, setOrientationSearchInput] = useState('');

    const [locationConfigSortColumn, setLocationConfigSortColumn] = useState("label");
    const [locationConfigSortOrder, setLocationConfigSortOrder] = useState(ASCENDING);

    const tableEndRef = useRef(null);

    const PAGE_CONFIGURATION_KEYS = [CONFIG_TYPE.VAPR_LOCATION_CONFIGURATION]

    useEffect(() => {
        if (!openAddLocationModal && !openDeleteLocationModal && !openLocationCSVModal) {
            getData()
            const interval = setInterval(() => {
                if (!document.hidden) {
                    getData();
                }
            }, getAssetRefreshInterval());
            return () => clearInterval(interval);
        }
    }, [openAddLocationModal, openDeleteLocationModal, openLocationCSVModal]);

    useEffect(() => {
        setDraftData({
            data: chainWalk(() => viewModel.locationConfig[selectedMac], []),
            stateKey: CONFIG_TYPE.VAPR_LOCATION_CONFIGURATION,
            subKey: selectedMac,
        })
    }, [selectedMac]);

    const getData = () => {
        if (isDataEdited(PAGE_CONFIGURATION_KEYS)) return;
        dispatch({type: 'LOAD_VIEW', data: {dispatcher: dispatch}});
    }

    const getModalContent = async () => {
        setIsVersionsLoading(true);
        let versionsData = await getAssetVersions(selectedMac);
        // A failed/timed-out fetch resolves with an error shape (never rejects); fall back to
        // an empty list so the rollback modal never crashes or spins forever.
        setRollbackVersions(chainWalk(() => versionsData.data.assetVersionDetailsList, []));
        setIsVersionsLoading(false);
    }

    const loadRollbackDataToDraft = async () => {
        let rollbackData = await getAssetByVersion(selectedMac, selectedVersion);
        const locationConfigData = chainWalk(() =>
            JSON.parse(rollbackData.data.asset.assetDetails.assetAttributes.locationMapping), []);
        if (locationConfigData.length) {
            setDraftData({
                data: locationConfigData,
                stateKey: CONFIG_TYPE.VAPR_LOCATION_CONFIGURATION,
                subKey: selectedMac,
                allRowsAdded: true,
                override: true
            });
        }
    }

    const updateLocationConfigDraftData = (locationConfig) => {
        setDraftData({
            data: chainWalk(() => locationConfig, []),
            stateKey: CONFIG_TYPE.VAPR_LOCATION_CONFIGURATION,
            subKey: selectedMac,
            allRowsAdded: true,
            override: true
        })
    }

    const deployDraftVaprAsset = () => {
        const locationConfigList = getFilteredDraftData({
            stateKey: CONFIG_TYPE.VAPR_LOCATION_CONFIGURATION,
            subKey: selectedMac
        })

        dispatch({
            type: 'UPDATE_VAPR_CONFIGURATION',
            data: {
                dispatcher: dispatch,
                showCustomAlert: showCustomAlert,
                configurations: {
                    locationConfig: locationConfigList
                },
                mac: selectedMac
            }
        });
        setDraftData({
            data: locationConfigList,
            stateKey: CONFIG_TYPE.VAPR_LOCATION_CONFIGURATION,
            subKey: selectedMac,
            override: true
        })
    }

    const scrollToBottom = () => {
        tableEndRef.current && tableEndRef.current.scrollIntoView({behavior: "smooth"})
    }

    const sortLocationConfigColumns = {
        label: (a, b, ascending) => {
            return ascending ? sortComparator(a.label, b.label) : -sortComparator(a.label, b.label);
        },
        locationType: (a, b, ascending) => {
            return ascending ? sortComparator(getLocationTypeDisplay(a.locationType), getLocationTypeDisplay(b.locationType))
                : -sortComparator(getLocationTypeDisplay(a.locationType), getLocationTypeDisplay(b.locationType));
        },
        orientation: (a, b, ascending) => {
            return ascending ? sortComparator(getOrientationDisplay(a.orientation), getOrientationDisplay(b.orientation))
                : -sortComparator(getOrientationDisplay(a.orientation), getOrientationDisplay(b.orientation));
        }
    }

    const searchDataForLocationConfigTable = () => {
        let data = getDraftData({
            stateKey: CONFIG_TYPE.VAPR_LOCATION_CONFIGURATION,
            subKey: selectedMac
        })

        // Label goes through the same always-a-string treatment as the display helpers, so a
        // malformed stored entry (schemaless NARC data) can never crash the search filter.
        data = data.filter(item => (item.label == null ? "" : String(item.label))
            .toLowerCase()
            .includes(locationIdSearchInput.toLowerCase()))
            .filter(item => getLocationTypeDisplay(item.locationType)
                .toLowerCase()
                .includes(locationTypeSearchInput.toLowerCase()))
            .filter(item => getOrientationDisplay(item.orientation)
                .toLowerCase()
                .includes(orientationSearchInput.toLowerCase()));

        data = data.sort((a, b) =>
            sortLocationConfigColumns[locationConfigSortColumn](a, b, locationConfigSortOrder === ASCENDING)
        )
        return data;
    }

    const getSortedDeviceList = () => {
        const sortedDeviceList = Object.values(viewModel.vaprDeviceInformation);
        sortedDeviceList && sortedDeviceList.sort((a, b) => sortComparator(a.name, b.name));
        return sortedDeviceList
    }

    const getHeaderProps = () => {
        return {
            selectedMac,
            setSelectedMac,
            valueConfiguration,
            setValueConfiguration,
            defaultSelection: "",
            resetDraftData,
            deployDraft: deployDraftVaprAsset,
            scrollToBottom,
            sortedDeviceList: getSortedDeviceList(),
            configurationKeys: PAGE_CONFIGURATION_KEYS,
            refreshDeps: [viewModel.timestamp],
            isDeployDisabled: !isDataEdited(PAGE_CONFIGURATION_KEYS),
            isRollbackDisabled: !selectedMac || isDataEdited(PAGE_CONFIGURATION_KEYS),
            rollbackClickHandler: () => {getModalContent(); setShowRollbackModal(true)}
        }
    }

    const renderLocationConfigTableRow = (config, rowNumber) => {
        const labelString = config.label;
        const prevLabelString = chainWalk(() => config.prev.label, null);
        const locationTypeString = getLocationTypeDisplay(config.locationType);
        const prevLocationTypeString = chainWalk(() => getLocationTypeDisplay(config.prev.locationType), null);
        const orientationString = getOrientationDisplay(config.orientation);
        const prevOrientationString = chainWalk(() => getOrientationDisplay(config.prev.orientation), null);

        const labelChanged = prevLabelString && labelString !== prevLabelString;
        const locationTypeChanged = prevLocationTypeString && locationTypeString !== prevLocationTypeString;
        const orientationChanged = prevOrientationString && orientationString !== prevOrientationString;

        return (
            <TableRow key={rowNumber} highlightOnHover={true} backgroundColor={OPERATION_COLOR[config.operation]}>
                <TableCell>
                    {labelChanged && <Text> {prevLabelString} </Text>}
                    {labelChanged && <Icon tokens={arrowRightTokens}/>}
                    <Text> {labelString} </Text>
                </TableCell>
                <TableCell>
                    {locationTypeChanged && <Text> {prevLocationTypeString} </Text>}
                    {locationTypeChanged && <Icon tokens={arrowRightTokens}/>}
                    <Text> {locationTypeString} </Text>
                </TableCell>
                <TableCell>
                    {orientationChanged && <Text> {prevOrientationString} </Text>}
                    {orientationChanged && <Icon tokens={arrowRightTokens}/>}
                    <Text> {orientationString} </Text>
                </TableCell>
            </TableRow>
        )
    }

    if (viewModel.loadingOverview) {
        return (<Loader/>)
    } else {
        return (
            <Column>
                <ConfigurationTabHeader {...getHeaderProps()}/>
                <RollbackModal
                    showRollbackModal={showRollbackModal}
                    setShowRollbackModal={setShowRollbackModal}
                    isVersionsLoading={isVersionsLoading}
                    selectedVersion={selectedVersion}
                    setSelectedVersion={setSelectedVersion}
                    rollBackVersions={rollBackVersions}
                    loadRollbackDataToDraft={loadRollbackDataToDraft}
                    intl={intl}
                />
                {valueConfiguration === CONFIG_TYPE.VAPR_LOCATION_CONFIGURATION && selectedMac &&
                    <Column>
                        <PaginatedTable
                            data={searchDataForLocationConfigTable()}
                            renderHeader={() => (
                                <TableRow highlightOnHover={false}>
                                    <Tooltip position="top" title={getLocalizedMessage({intl, stringId: "tooltip.vaprlocationid"})}>
                                        <TableCell sortColumn="label">
                                            {getLocalizedMessage({intl, stringId: "location.locationId"})}
                                        </TableCell>
                                    </Tooltip>
                                    <Tooltip position="top" title={getLocalizedMessage({intl, stringId: "tooltip.vaprlocationtype"})}>
                                        <TableCell sortColumn="locationType">
                                            {getLocalizedMessage({intl, stringId: "location.type"})}
                                        </TableCell>
                                    </Tooltip>
                                    <TableCell sortColumn="orientation">
                                        <Row spacing="100" alignmentVertical="center" wrap="none">
                                            <span>{getLocalizedMessage({intl, stringId: "location.orientation"})}</span>
                                            <VaprOrientationGuide/>
                                        </Row>
                                    </TableCell>
                                </TableRow>
                            )}
                            renderSearchRow={() => (
                                <TableRow backgroundColor={SEARCH_ROW_COLOR}>
                                    <TableCell>
                                        <Input
                                            className="input-search-class"
                                            value={locationIdSearchInput}
                                            onChange={setLocationIdSearchInput}
                                            type="text"
                                            placeholder="Search"
                                            width="70%"
                                            size="small"/>
                                    </TableCell>
                                    <TableCell>
                                        <Input
                                            className="input-search-class"
                                            value={locationTypeSearchInput}
                                            onChange={setLocationTypeSearchInput}
                                            type="text"
                                            placeholder="Search"
                                            width="70%"
                                            size="small"/>
                                    </TableCell>
                                    <TableCell>
                                        <Input
                                            className="input-search-class"
                                            value={orientationSearchInput}
                                            onChange={setOrientationSearchInput}
                                            type="text"
                                            placeholder="Search"
                                            width="70%"
                                            size="small"/>
                                    </TableCell>
                                </TableRow>
                            )}
                            renderRow={renderLocationConfigTableRow}
                            sortColumn={locationConfigSortColumn}
                            sortDirection={locationConfigSortOrder}
                            onSort={({sortColumn, sortDirection}) => {
                                setLocationConfigSortColumn(sortColumn);
                                setLocationConfigSortOrder(sortDirection);
                            }}
                        />
                        <Row width="100%" widths={["100", "100", "fill", "100"]}>
                            <Button
                                    onClick={() => setOpenAddLocationModal(true)}
                                    type="primary"
                                    size="small">
                                <Icon tokens={plusCircleTokens}/>
                                {getLocalizedMessage({intl, stringId: "vaprlocationconfig.addupdate"})}
                            </Button>
                            <Button
                                    onClick={() => setOpenLocationCSVModal(true)}
                                    type="primary"
                                    size="small">
                                <Icon tokens={uploadTokens}/>
                                {getLocalizedMessage({intl, stringId: "vaprlocationconfig.uploadCSV"})}
                            </Button>
                            <Box></Box>
                            <Button
                                    onClick={() => setOpenDeleteLocationModal(true)}
                                    size="small"
                                    type="tertiary">
                                <Icon tokens={trashTokens}/>
                                {getLocalizedMessage({intl, stringId: "vaprlocationconfig.deleteconfig"})}
                            </Button>
                        </Row>
                        <AddVaprLocationConfigurationModal mac={selectedMac}
                                                           open={openAddLocationModal}
                                                           setOpen={setOpenAddLocationModal}/>
                        <UploadFileModal mac={selectedMac}
                                         open={openLocationCSVModal} setOpen={setOpenLocationCSVModal}
                                         parser={VaprLocationConfigCSVParser} updateConfig={updateLocationConfigDraftData}/>
                        <DeleteConfigurationModal mac={selectedMac}
                                                  open={openDeleteLocationModal}
                                                  setOpen={setOpenDeleteLocationModal}
                                                  stateKey={CONFIG_TYPE.VAPR_LOCATION_CONFIGURATION}
                                                  keyExtractor={(entry) => entry.label}
                                                  titleStringId="vaprlocationconfig.deleteconfig"
                                                  promptStringId="modal.delete_location_config"
                                                  placeholderStringId="vaprlocationconfig.delete"
                                                  noMatchStringId="vaprlocationconfig.nomatch"
                                                  notAvailableStringId="vaprlocationconfig.notavailable"/>
                        <div ref={tableEndRef}/>
                    </Column>
                }
            </Column>
        )
    }
}

export default injectIntl(VaprDeviceConfiguration)



// WEBPACK FOOTER //
// ./src/components/devicemanagement/vaprdevice/VaprDeviceConfiguration.js